import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiService } from '../services/apiService'

interface Item {
  id: string
  description: string
  quantity: number
  unitPriceUSD: number
  totalValueUSD: number
  countryOfOrigin: string
  htsCode: string
  weight?: string
  dimensions?: string
}

interface AddressInfo {
  name: string
  name2?: string
  address: string
  city: string
  state?: string
  country: string
  postalCode: string
  phone?: string
  email?: string
  canadian_business_number?: string
}

interface StaffManualInvoiceGeneratorProps {
  token: string
  onBack: () => void
}

export default function StaffManualInvoiceGenerator({ token, onBack }: StaffManualInvoiceGeneratorProps) {
  const navigate = useNavigate()
  
  // Invoice metadata
  const [invoiceNumber, setInvoiceNumber] = useState('')
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0])
  const [shippingDate, setShippingDate] = useState(new Date().toISOString().split('T')[0])
  const [clientId, setClientId] = useState('')

  // Address information
  const [shipper, setShipper] = useState<AddressInfo>({
    name: 'YYZ PREP',
    name2: '13216730 Canada Inc.',
    address: '5-25 NEWKIRK CT',
    city: 'BRAMPTON, ON',
    country: 'CA',
    postalCode: 'L6Z 0B5',
    phone: '647 250 0111',
    email: 'hello@yyzprep.ca'
  })

  const [importer, setImporter] = useState<AddressInfo>({
    name: '',
    address: '',
    city: '',
    state: '',
    country: 'US',
    postalCode: '',
    phone: '',
    email: ''
  })

  const [destination, setDestination] = useState<AddressInfo>({
    name: '',
    address: '',
    city: '',
    state: '',
    country: 'US',
    postalCode: ''
  })

  const [broker, setBroker] = useState<AddressInfo>({
    name: 'ACB Customs',
    address: '6424A Danville Rd',
    city: 'Mississauga, ON',
    country: 'CA',
    postalCode: 'L5T 2S6',
    phone: '+1 (905) 564-1511',
    email: 'info@acbcustoms.com'
  })

  // Items and AI parsing
  const [items, setItems] = useState<Item[]>([])
  const [itemText, setItemText] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [aiParsing, setAiParsing] = useState(false)

  // Generate default invoice number on mount
  useEffect(() => {
    if (!invoiceNumber) {
      const now = new Date()
      const dateStr = now.getFullYear().toString() + 
                     (now.getMonth() + 1).toString().padStart(2, '0') + 
                     now.getDate().toString().padStart(2, '0')
      const timeStr = now.getHours().toString().padStart(2, '0') + 
                     now.getMinutes().toString().padStart(2, '0')
      setInvoiceNumber(`STAFF-MANUAL-${dateStr}-${timeStr}`)
    }
  }, [invoiceNumber])

  const handleParseItems = async () => {
    if (!itemText.trim()) {
      setError('Please enter item information to parse')
      return
    }

    setAiParsing(true)
    setError(null)

    try {
      const response = await apiService.parseStaffItemsWithAI(itemText, invoiceDate, shippingDate)
      
      if (response.success && response.data) {
        const parsedItems = response.data.items.map((item: any, index: number) => ({
          id: `item-${Date.now()}-${index}`,
          description: item.description || '',
          quantity: item.quantity || 1,
          unitPriceUSD: item.unitPriceUSD || 0,
          totalValueUSD: item.totalValueUSD || 0,
          countryOfOrigin: item.countryOfOrigin || 'TBD',
          htsCode: item.htsCode || 'TBD',
          weight: item.weight || 'TBD',
          dimensions: item.dimensions || 'TBD'
        }))
        
        setItems(parsedItems)
        setSuccess(`Successfully parsed ${parsedItems.length} items with AI`)
      } else {
        setError(response.error || 'Failed to parse items')
      }
    } catch (error) {
      console.error('Staff AI parsing error:', error)
      setError('Failed to parse items with AI')
    } finally {
      setAiParsing(false)
    }
  }

  const addManualItem = () => {
    const newItem: Item = {
      id: `item-${Date.now()}`,
      description: '',
      quantity: 1,
      unitPriceUSD: 0,
      totalValueUSD: 0,
      countryOfOrigin: 'TBD',
      htsCode: 'TBD',
      weight: 'TBD',
      dimensions: 'TBD'
    }
    setItems([...items, newItem])
  }

  const updateItem = (id: string, field: keyof Item, value: any) => {
    setItems(items.map(item => {
      if (item.id === id) {
        const updatedItem = { ...item, [field]: value }
        
        // Auto-calculate total when quantity or unit price changes
        if (field === 'quantity' || field === 'unitPriceUSD') {
          updatedItem.totalValueUSD = updatedItem.quantity * updatedItem.unitPriceUSD
        }
        
        return updatedItem
      }
      return item
    }))
  }

  const removeItem = (id: string) => {
    setItems(items.filter(item => item.id !== id))
  }

  const calculateTotalValue = () => {
    return items.reduce((sum, item) => sum + item.totalValueUSD, 0)
  }

  const updateAddress = (addressType: 'shipper' | 'importer' | 'destination' | 'broker', field: keyof AddressInfo, value: string) => {
    const setters = {
      shipper: setShipper,
      importer: setImporter,
      destination: setDestination,
      broker: setBroker
    }
    
    const getters = {
      shipper,
      importer,
      destination,
      broker
    }
    
    setters[addressType]({
      ...getters[addressType],
      [field]: value
    })
  }

  const handleGenerateInvoice = async () => {
    if (items.length === 0) {
      setError('Please add at least one item to generate an invoice')
      return
    }

    // Client ID is now optional

    setLoading(true)
    setError(null)
    setSuccess(null)

    try {
      const invoiceData = {
        invoiceData: { items },
        shipper,
        importer,
        destination,
        broker,
        invoiceNumber,
        invoiceDate,
        shippingDate,
        clientId: clientId.trim() || null
      }

      const response = await apiService.generateStaffManualInvoice(invoiceData)
      
      if (response.success) {
        setSuccess('Staff invoice generated successfully! Download will start automatically.')
        
        // Download the generated invoice
        if (response.data?.filename) {
          console.log('🔄 Starting download for filename:', response.data.filename)
          await apiService.downloadStaffInvoice(response.data.filename)
          console.log('✅ Download completed')
        } else {
          console.warn('⚠️ No filename received in response:', response.data)
        }
      } else {
        setError(response.error || 'Failed to generate staff invoice')
      }
    } catch (error) {
      console.error('Staff invoice generation error:', error)
      setError('Failed to generate staff invoice')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="staff-manual-invoice-generator">
      <div className="card">
        <div className="flex justify-between items-center mb-4">
          <h2>Staff Manual Invoice Generator</h2>
          <button 
            className="button secondary"
            onClick={onBack}
          >
            ← Back to Staff Dashboard
          </button>
        </div>
        
        <p>Create custom commercial invoices for any client with AI-powered item parsing</p>

        {/* Success/Error Messages */}
        {error && (
          <div className="error" style={{ marginBottom: '20px' }}>
            {error}
          </div>
        )}
        
        {success && (
          <div className="success" style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#d4edda', color: '#155724', border: '1px solid #c3e6cb', borderRadius: '4px' }}>
            {success}
          </div>
        )}

        {/* Client ID and Invoice Metadata */}
        <div className="section">
          <h3>Invoice Information</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '15px' }}>
            <div>
              <label>Client ID (Optional):</label>
              <input
                type="text"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                className="form-control"
                placeholder="Enter client ID (optional)"
                style={{ backgroundColor: '#f8f9fa' }}
              />
            </div>
            <div>
              <label>Invoice Number:</label>
              <input
                type="text"
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                className="form-control"
              />
            </div>
            <div>
              <label>Invoice Date:</label>
              <input
                type="date"
                value={invoiceDate}
                onChange={(e) => setInvoiceDate(e.target.value)}
                className="form-control"
              />
            </div>
            <div>
              <label>Shipping Date:</label>
              <input
                type="date"
                value={shippingDate}
                onChange={(e) => setShippingDate(e.target.value)}
                className="form-control"
              />
            </div>
          </div>
        </div>

        {/* Address Sections */}
        <div className="section">
          <h3>Address Information</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
            {/* Shipper */}
            <div>
              <h4>Shipper (YYZ PREP)</h4>
              <div style={{ display: 'grid', gap: '10px' }}>
                <input
                  type="text"
                  value={shipper.name}
                  onChange={(e) => updateAddress('shipper', 'name', e.target.value)}
                  className="form-control"
                  placeholder="Company Name"
                />
                <input
                  type="text"
                  value={shipper.address}
                  onChange={(e) => updateAddress('shipper', 'address', e.target.value)}
                  className="form-control"
                  placeholder="Address"
                />
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '10px' }}>
                  <input
                    type="text"
                    value={shipper.city}
                    onChange={(e) => updateAddress('shipper', 'city', e.target.value)}
                    className="form-control"
                    placeholder="City, Province"
                  />
                  <input
                    type="text"
                    value={shipper.postalCode}
                    onChange={(e) => updateAddress('shipper', 'postalCode', e.target.value)}
                    className="form-control"
                    placeholder="Postal Code"
                  />
                </div>
              </div>
            </div>

            {/* Importer */}
            <div>
              <h4>Importer</h4>
              <div style={{ display: 'grid', gap: '10px' }}>
                <input
                  type="text"
                  value={importer.name}
                  onChange={(e) => updateAddress('importer', 'name', e.target.value)}
                  className="form-control"
                  placeholder="Importer Company Name"
                />
                <input
                  type="text"
                  value={importer.address}
                  onChange={(e) => updateAddress('importer', 'address', e.target.value)}
                  className="form-control"
                  placeholder="Address"
                />
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '10px' }}>
                  <input
                    type="text"
                    value={importer.city}
                    onChange={(e) => updateAddress('importer', 'city', e.target.value)}
                    className="form-control"
                    placeholder="City"
                  />
                  <input
                    type="text"
                    value={importer.state || ''}
                    onChange={(e) => updateAddress('importer', 'state', e.target.value)}
                    className="form-control"
                    placeholder="State"
                  />
                  <input
                    type="text"
                    value={importer.postalCode}
                    onChange={(e) => updateAddress('importer', 'postalCode', e.target.value)}
                    className="form-control"
                    placeholder="ZIP"
                  />
                </div>
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            {/* Destination */}
            <div>
              <h4>Destination Address</h4>
              <div style={{ display: 'grid', gap: '10px' }}>
                <input
                  type="text"
                  value={destination.name}
                  onChange={(e) => updateAddress('destination', 'name', e.target.value)}
                  className="form-control"
                  placeholder="Destination Company/Person"
                />
                <input
                  type="text"
                  value={destination.address}
                  onChange={(e) => updateAddress('destination', 'address', e.target.value)}
                  className="form-control"
                  placeholder="Address"
                />
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '10px' }}>
                  <input
                    type="text"
                    value={destination.city}
                    onChange={(e) => updateAddress('destination', 'city', e.target.value)}
                    className="form-control"
                    placeholder="City"
                  />
                  <input
                    type="text"
                    value={destination.state || ''}
                    onChange={(e) => updateAddress('destination', 'state', e.target.value)}
                    className="form-control"
                    placeholder="State"
                  />
                  <input
                    type="text"
                    value={destination.postalCode}
                    onChange={(e) => updateAddress('destination', 'postalCode', e.target.value)}
                    className="form-control"
                    placeholder="ZIP"
                  />
                </div>
              </div>
            </div>

            {/* Customs Broker */}
            <div>
              <h4>Customs Broker</h4>
              <div style={{ display: 'grid', gap: '10px' }}>
                <input
                  type="text"
                  value={broker.name}
                  onChange={(e) => updateAddress('broker', 'name', e.target.value)}
                  className="form-control"
                  placeholder="Broker Company Name"
                />
                <input
                  type="text"
                  value={broker.address}
                  onChange={(e) => updateAddress('broker', 'address', e.target.value)}
                  className="form-control"
                  placeholder="Address"
                />
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '10px' }}>
                  <input
                    type="text"
                    value={broker.city}
                    onChange={(e) => updateAddress('broker', 'city', e.target.value)}
                    className="form-control"
                    placeholder="City, Province"
                  />
                  <input
                    type="text"
                    value={broker.postalCode}
                    onChange={(e) => updateAddress('broker', 'postalCode', e.target.value)}
                    className="form-control"
                    placeholder="Postal Code"
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <input
                    type="text"
                    value={broker.phone || ''}
                    onChange={(e) => updateAddress('broker', 'phone', e.target.value)}
                    className="form-control"
                    placeholder="Phone"
                  />
                  <input
                    type="text"
                    value={broker.email || ''}
                    onChange={(e) => updateAddress('broker', 'email', e.target.value)}
                    className="form-control"
                    placeholder="Email"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* AI Item Parsing */}
        <div className="section">
          <h3>Add Items with AI</h3>
          <p>Paste your item information below and let AI parse it into structured data:</p>
          
          <textarea
            value={itemText}
            onChange={(e) => setItemText(e.target.value)}
            placeholder="Paste item information here (e.g., product lists, invoices, packing lists, etc.)..."
            className="form-control"
            style={{ minHeight: '100px', marginBottom: '10px' }}
          />
          
          <button 
            onClick={handleParseItems}
            disabled={aiParsing || !itemText.trim()}
            className="button primary"
            style={{ marginRight: '10px' }}
          >
            {aiParsing ? 'Parsing with AI...' : 'Parse Items with AI'}
          </button>
          
          <button 
            onClick={addManualItem}
            className="button secondary"
          >
            Add Manual Item
          </button>
        </div>

        {/* Items Table */}
        {items.length > 0 && (
          <div className="section">
            <h3>Items ({items.length})</h3>
            <div style={{ overflowX: 'auto' }}>
              <table className="items-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <th style={{ padding: '8px', border: '1px solid #ddd', minWidth: '200px' }}>Description</th>
                    <th style={{ padding: '8px', border: '1px solid #ddd', width: '80px' }}>Qty</th>
                    <th style={{ padding: '8px', border: '1px solid #ddd', width: '100px' }}>Unit Price</th>
                    <th style={{ padding: '8px', border: '1px solid #ddd', width: '100px' }}>Total</th>
                    <th style={{ padding: '8px', border: '1px solid #ddd', width: '80px' }}>Country</th>
                    <th style={{ padding: '8px', border: '1px solid #ddd', width: '100px' }}>HTS Code</th>
                    <th style={{ padding: '8px', border: '1px solid #ddd', width: '60px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id}>
                      <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                        <input
                          type="text"
                          value={item.description}
                          onChange={(e) => updateItem(item.id, 'description', e.target.value)}
                          className="form-control"
                          style={{ width: '100%' }}
                        />
                      </td>
                      <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateItem(item.id, 'quantity', parseInt(e.target.value) || 1)}
                          className="form-control"
                          style={{ width: '100%' }}
                          min="1"
                        />
                      </td>
                      <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                        <input
                          type="number"
                          value={item.unitPriceUSD}
                          onChange={(e) => updateItem(item.id, 'unitPriceUSD', parseFloat(e.target.value) || 0)}
                          className="form-control"
                          style={{ width: '100%' }}
                          step="0.01"
                          min="0"
                        />
                      </td>
                      <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                        ${item.totalValueUSD.toFixed(2)}
                      </td>
                      <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                        <input
                          type="text"
                          value={item.countryOfOrigin}
                          onChange={(e) => updateItem(item.id, 'countryOfOrigin', e.target.value)}
                          className="form-control"
                          style={{ width: '100%' }}
                        />
                      </td>
                      <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                        <input
                          type="text"
                          value={item.htsCode}
                          onChange={(e) => updateItem(item.id, 'htsCode', e.target.value)}
                          className="form-control"
                          style={{ width: '100%' }}
                        />
                      </td>
                      <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                        <button
                          onClick={() => removeItem(item.id)}
                          className="button danger"
                          style={{ padding: '4px 8px', fontSize: '12px' }}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div style={{ marginTop: '15px', textAlign: 'right' }}>
              <strong>Total Value: ${calculateTotalValue().toFixed(2)} USD</strong>
            </div>
          </div>
        )}

        {/* Generate Invoice Button */}
        <div className="section" style={{ textAlign: 'center', paddingTop: '20px', borderTop: '1px solid #ddd' }}>
          <button 
            onClick={handleGenerateInvoice}
            disabled={loading || items.length === 0}
            className="button primary"
            style={{ fontSize: '16px', padding: '12px 24px' }}
          >
            {loading ? 'Generating Staff Invoice...' : 'Generate Staff Invoice'}
          </button>
          
          {items.length === 0 && (
            <p style={{ marginTop: '10px', color: '#666', fontSize: '14px' }}>
              Add items above to generate an invoice. Client ID is optional.
            </p>
          )}
        </div>
      </div>

      <style jsx>{`
        .staff-manual-invoice-generator {
          max-width: 1200px;
          margin: 0 auto;
          padding: 20px;
        }
        
        .section {
          margin-bottom: 30px;
          padding-bottom: 20px;
        }
        
        .section h3 {
          margin-bottom: 15px;
          color: #333;
          border-bottom: 2px solid #007bff;
          padding-bottom: 5px;
        }
        
        .section h4 {
          margin-bottom: 10px;
          color: #666;
          font-size: 14px;
          font-weight: 600;
        }
        
        .form-control {
          width: 100%;
          padding: 8px 12px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 14px;
        }
        
        .form-control:focus {
          outline: none;
          border-color: #007bff;
          box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
        }
        
        .button {
          padding: 8px 16px;
          border: none;
          border-radius: 4px;
          font-size: 14px;
          cursor: pointer;
          transition: background-color 0.2s;
        }
        
        .button.primary {
          background-color: #007bff;
          color: white;
        }
        
        .button.primary:hover {
          background-color: #0056b3;
        }
        
        .button.primary:disabled {
          background-color: #6c757d;
          cursor: not-allowed;
        }
        
        .button.secondary {
          background-color: #6c757d;
          color: white;
        }
        
        .button.secondary:hover {
          background-color: #545b62;
        }
        
        .button.danger {
          background-color: #dc3545;
          color: white;
        }
        
        .button.danger:hover {
          background-color: #c82333;
        }
        
        .items-table th {
          font-weight: 600;
          text-align: left;
        }
        
        .items-table td input {
          border: none;
          padding: 4px;
          font-size: 13px;
        }
        
        .items-table td input:focus {
          outline: 2px solid #007bff;
        }
      `}</style>
    </div>
  )
}